import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Checkbox } from '../ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  Loader2,
  Search,
  Calendar,
  Users,
  ClipboardList,
  Settings,
  UserPlus,
  Printer,
  Check,
  ExternalLink,
  X,
  ShieldCheck,
  AlertTriangle,
  Wand2,
  ArrowRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import EventEndpoints from '../../endpoints/EventEndpoints'
import IdentityEndpoints from '../../endpoints/IdentityEndpoints'
import { Event as EventModel, IdentityRole, AccreditationLevel } from '../../model/Event'
import { QuickProvisioningIdentity } from '../../model/Identity'
import { useSessionStore } from '../../stores/useSessionStore'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { BadgePrintingLogic } from '../../common/BadgePrintingLogic'
import { CreateIdentityDialog } from '../identity/CreateIdentityDialogModern'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { SetupPrerequisiteAlert } from './setup/SetupPrerequisiteAlert'
import { SyncedTopScrollbar } from '../common/SyncedTopScrollbar'
import { Combobox } from '../ui/combobox'
import {
  computeEventSetupStatus,
  setupStepHref,
  wizardHref,
} from './setup/eventSetupStatus'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'

const ALL_ID = -1
const NOT_ASSIGNED_ID = 0
const DEFAULT_ACCREDITATION_ID = -1
const MAX_VISIBLE = 50

// The role matrix adds a column per role, which pushes the per-person controls off
// the right edge of the screen. Those three columns are pinned to the right instead,
// so only the radio matrix scrolls. The sticky offsets are derived from the widths
// below so a column and its neighbours' offsets can't drift apart, and the controls
// inside get a fixed width of their own — a long accreditation level name would
// otherwise widen the column and throw every offset out.
const PIN_CONTROL_WIDTH = 132
const PIN_WIDTH = {
  accreditation: 150,
  secondaryRole: 150,
  print: 52,
}
const PIN_RIGHT = {
  accreditation: PIN_WIDTH.print + PIN_WIDTH.secondaryRole,
  secondaryRole: PIN_WIDTH.print,
  print: 0,
}

export const QuickProvisioningModern: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const { hasAdmin, hasIdentityManagerFor } = useSessionStore()

  const [event, setEvent] = useState<EventModel | null>(null)
  const [identities, setIdentities] = useState<QuickProvisioningIdentity[]>([])
  const [loading, setLoading] = useState(true)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>(ALL_ID.toString())
  const [includeGlobal, setIncludeGlobal] = useState(true)
  const [showAll, setShowAll] = useState(false)

  // Bulk provision
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false)
  const [bulkRoleId, setBulkRoleId] = useState<number | null>(null)
  const [bulkSaving, setBulkSaving] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [batchDialogOpen, setBatchDialogOpen] = useState(false)
  const [showUnusedRoles, setShowUnusedRoles] = useState(false)

  const { clients } = useApplicationStore()

  // The grid is wider than the viewport once an event has more than a handful of
  // roles, so it gets its own scroll frame: a mirrored scrollbar above it and a
  // frozen header row inside it.
  const tableScrollRef = useRef<HTMLDivElement>(null)

  // Load data
  useEffect(() => {
    if (!eventId) return
    const load = async () => {
      setLoading(true)
      try {
        const [eventData, identityData] = await Promise.all([
          EventEndpoints.getEvent(parseInt(eventId)),
          IdentityEndpoints.getPartialIdentityList(parseInt(eventId), true),
        ])
        setEvent(eventData)
        setIdentities(identityData)
      } catch (error) {
        console.error('Failed to load provisioning data:', error)
        toast.error(extractErrorMessage(error, 'Failed to load provisioning data'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [eventId])

  // Ordered roles: schedulable first, then non-schedulable, each sorted alphabetically
  const orderedRoles = useMemo(() => {
    if (!event) return []
    const schedulable = event.roles.filter((r) => r.schedulable).sort((a, b) => a.internalName.localeCompare(b.internalName))
    const nonSchedulable = event.roles.filter((r) => !r.schedulable).sort((a, b) => a.internalName.localeCompare(b.internalName))
    return [...schedulable, ...nonSchedulable]
  }, [event])

  const accreditationLevels = useMemo(() => {
    if (!event) return []
    return [...event.accreditationLevels].sort((a, b) => a.name.localeCompare(b.name))
  }, [event])

  // Provisioning is only useful once the event has a schedulable role, so we
  // say that up front rather than letting the user fill in a grid that can't
  // produce anybody the schedule will accept.
  const setupStatus = useMemo(() => computeEventSetupStatus(event), [event])
  const hasSchedulableRole = setupStatus.byId.roles.done

  // Filtered identities
  const filtered = useMemo(() => {
    let result = identities

    // Global filter
    if (!includeGlobal) {
      result = result.filter((i) => i.parentCustomer != null)
    }

    // Role filter
    const roleFilterNum = parseInt(roleFilter)
    if (roleFilterNum === NOT_ASSIGNED_ID) {
      result = result.filter((i) => !i.roleId)
    } else if (roleFilterNum !== ALL_ID) {
      result = result.filter((i) => i.roleId === roleFilterNum)
    }

    // Search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().replace(/[\s-]/g, '')
      result = result.filter((i) => {
        const full = `${i.firstName}${i.middleName || ''}${i.lastName}${i.email || ''}`.toLowerCase().replace(/[\s-]/g, '')
        return full.includes(term)
      })
    }

    // Sort by first name, then last name as tiebreaker
    return result.sort(
      (a, b) =>
        a.firstName.localeCompare(b.firstName) ||
        a.lastName.localeCompare(b.lastName)
    )
  }, [identities, includeGlobal, roleFilter, searchTerm])

  const visibleIdentities = showAll ? filtered : filtered.slice(0, MAX_VISIBLE)
  const unassignedCount = identities.filter((i) => !i.roleId).length

  // One pass rather than a filter per role — an event can carry dozens of roles
  // and thousands of identities.
  const roleCounts = useMemo(() => {
    const counts = new Map<number, number>()
    identities.forEach((i) => {
      if (i.roleId) counts.set(i.roleId, (counts.get(i.roleId) ?? 0) + 1)
    })
    return counts
  }, [identities])

  // Roles nobody is provisioned to say nothing useful and, on an event with 30+
  // roles, push everything else off the screen. They stay behind a toggle.
  const usedRoles = useMemo(
    () => orderedRoles.filter((r) => (roleCounts.get(r.id) ?? 0) > 0),
    [orderedRoles, roleCounts]
  )
  const unusedRoleCount = orderedRoles.length - usedRoles.length
  const roleCountChips = showUnusedRoles ? orderedRoles : usedRoles

  const bulkRoleOptions = useMemo(
    () =>
      orderedRoles.map((r) => ({
        value: r.id.toString(),
        label: r.internalName,
        sublabel: r.schedulable ? 'Schedulable' : undefined,
      })),
    [orderedRoles]
  )

  // Provision a single identity to a role
  const handleProvision = async (identityId: number, roleId: number) => {
    if (!event) return
    const identity = identities.find((i) => i.id === identityId)
    try {
      await IdentityEndpoints.provisionForEvent(identityId, event.id, {
        roleId,
        accreditationLevelId: identity?.accreditationLevelId ?? null,
        secondaryRoleName: identity?.secondaryRoleName || '',
      }, true)
      setIdentities((prev) =>
        prev.map((i) => (i.id === identityId ? { ...i, roleId } : i))
      )
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to provision identity'))
    }
  }

  // Unprovision a single identity
  const handleUnprovision = async (identityId: number) => {
    if (!event) return
    try {
      await IdentityEndpoints.deleteEventProvisioning(identityId, event.id)
      setIdentities((prev) =>
        prev.map((i) =>
          i.id === identityId
            ? { ...i, roleId: null as any, secondaryRoleName: '', accreditationLevelId: null }
            : i
        )
      )
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to unprovision identity'))
    }
  }

  // Update accreditation level
  const handleAccreditationChange = async (identityId: number, accIdStr: string) => {
    if (!event) return
    const identity = identities.find((i) => i.id === identityId)
    if (!identity || !identity.roleId) return
    const accId = parseInt(accIdStr)
    try {
      await IdentityEndpoints.provisionForEvent(identityId, event.id, {
        roleId: identity.roleId,
        accreditationLevelId: accId === DEFAULT_ACCREDITATION_ID ? null : accId,
        secondaryRoleName: identity.secondaryRoleName || '',
      }, true)
      setIdentities((prev) =>
        prev.map((i) =>
          i.id === identityId
            ? { ...i, accreditationLevelId: accId === DEFAULT_ACCREDITATION_ID ? null : accId }
            : i
        )
      )
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to update accreditation'))
    }
  }

  // Update secondary role name
  const handleSecondaryRoleBlur = async (identityId: number, value: string) => {
    if (!event) return
    const identity = identities.find((i) => i.id === identityId)
    if (!identity || !identity.roleId || identity.secondaryRoleName === value) return
    try {
      await IdentityEndpoints.provisionForEvent(identityId, event.id, {
        roleId: identity.roleId,
        accreditationLevelId: identity.accreditationLevelId ?? null,
        secondaryRoleName: value,
      }, true)
      setIdentities((prev) =>
        prev.map((i) => (i.id === identityId ? { ...i, secondaryRoleName: value } : i))
      )
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to update secondary role'))
    }
  }

  // Bulk provision all unassigned
  const handleBulkProvision = async () => {
    if (!event || !bulkRoleId) return
    const unassigned = identities.filter((i) => !i.roleId)
    if (unassigned.length === 0) {
      toast.error('No unassigned identities to provision')
      return
    }

    setBulkSaving(true)
    try {
      await IdentityEndpoints.provisionMultipleForEvent(event.id, {
        roleId: bulkRoleId,
        accreditationLevelId: null,
        secondaryRoleName: '',
        identityIds: unassigned.map((i) => i.id),
      })
      setIdentities((prev) =>
        prev.map((i) =>
          !i.roleId ? { ...i, roleId: bulkRoleId } : i
        )
      )
      toast.success(`${unassigned.length} identities provisioned`)
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to bulk provision'))
    } finally {
      setBulkSaving(false)
      setBulkDialogOpen(false)
      setBulkRoleId(null)
    }
  }

  // Print badge
  const handlePrintBadge = (identityId: number) => {
    if (!eventId) return
    BadgePrintingLogic(() =>
      IdentityEndpoints.getBadge(identityId, parseInt(eventId))
    ).catch((error) => toast.error(extractErrorMessage(error, 'Failed to print badge')))
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">Loading provisioning data...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p className="text-center text-muted-foreground">Event not found</p>
      </div>
    )
  }

  const isAdmin = hasAdmin()
  // Mirror backend: provisioning identities to event roles requires IDENTITY_MANAGER+ for the event's client.
  const canProvision = event.client
    ? hasIdentityManagerFor(event.client.id)
    : false

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/event">Events</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href={`/event/${eventId}/config`}>{event.name}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-semibold">Quick Provisioning</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <ClipboardList className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">Quick Provisioning</CardTitle>
                <CardDescription className="flex items-center gap-3 mt-1">
                  <span>{event.name}</span>
                  <Badge variant="secondary">{event.client?.name}</Badge>
                  <span className="text-muted-foreground">
                    {identities.length} identities / {unassignedCount} unassigned
                  </span>
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              {setupStatus.firstIncompleteStepId && (
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(
                      `${wizardHref(eventId!)}?from=/event/${eventId}/provision`
                    )
                  }
                  title="Walk through the remaining setup steps"
                >
                  <Wand2 className="mr-2 h-4 w-4" />
                  Setup Wizard
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate(`/event/${eventId}/config`)}>
                <Settings className="mr-2 h-4 w-4" />
                Configure
              </Button>
              <Button variant="outline" onClick={() => navigate(`/event/${eventId}/schedule`)}>
                <Calendar className="mr-2 h-4 w-4" />
                Schedule
              </Button>
              <Button
                variant="outline"
                onClick={() => setBatchDialogOpen(true)}
              >
                <Printer className="mr-2 h-4 w-4" />
                Print All Badges
              </Button>
              {canProvision && (
                <Button onClick={() => setShowCreateDialog(true)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  New Identity
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Setup prerequisites */}
      {orderedRoles.length === 0 ? (
        <SetupPrerequisiteAlert
          eventId={eventId!}
          missing={['roles']}
          action="provision anyone onto this event"
        />
      ) : (
        !hasSchedulableRole && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>No schedulable roles on this event</AlertTitle>
            <AlertDescription className="space-y-3">
              <p className="text-muted-foreground">
                People can still be provisioned for badging, but only roles marked
                "Schedulable" produce staff who can be assigned to shifts. Nobody
                provisioned here will appear on the schedule until at least one role
                is schedulable.
              </p>
              <Button asChild size="sm" variant="outline">
                <Link to={setupStepHref('roles', eventId!)}>
                  Review roles
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Link>
              </Button>
            </AlertDescription>
          </Alert>
        )
      )}

      {/* Filters & Bulk Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_ID.toString()}>All Roles</SelectItem>
                <SelectItem value={NOT_ASSIGNED_ID.toString()}>Unassigned Only</SelectItem>
                {orderedRoles.map((r) => (
                  <SelectItem key={r.id} value={r.id.toString()}>
                    {r.internalName}
                    {r.schedulable ? ' (S)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeGlobalQP"
                  checked={includeGlobal}
                  onCheckedChange={(checked) => setIncludeGlobal(checked === true)}
                />
                <label htmlFor="includeGlobalQP" className="text-sm font-medium whitespace-nowrap">
                  Include Global
                </label>
              </div>
            )}
            {canProvision &&
              unassignedCount > 0 &&
              orderedRoles.length > 0 && (
                <Combobox
                  className="ml-auto w-[260px]"
                  // An action, not a stored value: the trigger always shows the
                  // prompt, and picking a role opens the confirmation.
                  value=""
                  onChange={(roleId) => {
                    setBulkRoleId(parseInt(roleId))
                    setBulkDialogOpen(true)
                  }}
                  options={bulkRoleOptions}
                  placeholder={`Bulk assign ${unassignedCount} to…`}
                  searchPlaceholder="Search roles…"
                  emptyText="No roles match"
                />
              )}
          </div>
        </CardContent>
      </Card>

      {/* Role Legend */}
      <div className="flex flex-wrap items-center gap-2">
        {roleCountChips.map((role) => (
          <Badge
            key={role.id}
            variant="outline"
            className={cn(
              'text-xs',
              role.schedulable ? 'border-cyan-500 text-cyan-700' : 'border-gray-300 text-gray-600'
            )}
          >
            {role.schedulable && <Calendar className="h-3 w-3 mr-1" />}
            {role.internalName}: {roleCounts.get(role.id) ?? 0}
          </Badge>
        ))}
        <Badge variant="secondary" className="text-xs">
          Unassigned: {unassignedCount}
        </Badge>
        {unusedRoleCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground"
            onClick={() => setShowUnusedRoles((v) => !v)}
          >
            {showUnusedRoles
              ? 'Hide unused roles'
              : `+${unusedRoleCount} unused ${unusedRoleCount === 1 ? 'role' : 'roles'}`}
          </Button>
        )}
      </div>

      {/* Provisioning Table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Users className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'No identities match your search' : 'No identities available for this event'}
              </p>
            </div>
          ) : (
            <>
              <SyncedTopScrollbar
                targetRef={tableScrollRef}
                className="border-b bg-muted/30"
              />
              <Table
                containerRef={tableScrollRef}
                containerClassName="max-h-[70vh] always-show-scrollbar"
              >
                {/* Sticky on the cells rather than the <thead>: with
                    border-collapse a sticky section header is unreliable. */}
                <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="sticky left-0 bg-background z-30 min-w-[200px] border-r">Name</TableHead>
                    <TableHead className="text-center w-[50px] bg-background z-20" title="Terms & conditions agreed digitally">
                      T&C
                    </TableHead>
                    <TableHead className="text-center min-w-[100px] bg-gray-100 z-20">
                      Not Assigned
                    </TableHead>
                    {orderedRoles.map((role) => (
                      <TableHead
                        key={role.id}
                        className={cn(
                          'text-center min-w-[110px] z-20',
                          role.schedulable ? 'bg-cyan-50' : 'bg-gray-50'
                        )}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          {role.schedulable && <Calendar className="h-3 w-3 text-cyan-600" />}
                          <span className="text-xs leading-tight">{role.internalName}</span>
                        </div>
                      </TableHead>
                    ))}
                    <TableHead
                      className="sticky bg-background z-30 px-2 border-l"
                      style={{
                        width: PIN_WIDTH.accreditation,
                        minWidth: PIN_WIDTH.accreditation,
                        right: PIN_RIGHT.accreditation,
                      }}
                    >
                      Accreditation
                    </TableHead>
                    <TableHead
                      className="sticky bg-background z-30 px-2"
                      style={{
                        width: PIN_WIDTH.secondaryRole,
                        minWidth: PIN_WIDTH.secondaryRole,
                        right: PIN_RIGHT.secondaryRole,
                      }}
                    >
                      Secondary Role
                    </TableHead>
                    <TableHead
                      className="sticky bg-background z-30 px-2"
                      style={{
                        width: PIN_WIDTH.print,
                        minWidth: PIN_WIDTH.print,
                        right: PIN_RIGHT.print,
                      }}
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleIdentities.map((identity) => {
                    const isProvisioned = !!identity.roleId
                    return (
                      <TableRow key={identity.id}>
                        <TableCell className="sticky left-0 bg-background z-10 border-r">
                          <Link
                            to={`/identity/${identity.id}`}
                            className="text-sm font-medium text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {identity.firstName} {identity.lastName}
                          </Link>
                          {!identity.parentCustomer && isAdmin && (
                            <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">G</Badge>
                          )}
                        </TableCell>
                        {/* Terms & conditions */}
                        <TableCell className="text-center">
                          {identity.termsAcknowledged && (
                            <ShieldCheck
                              className="h-4 w-4 text-green-600 inline-block"
                              aria-label="Terms & conditions agreed"
                            />
                          )}
                        </TableCell>
                        {/* Not Assigned */}
                        <TableCell
                          className={cn(
                            'text-center select-none',
                            canProvision &&
                              'cursor-pointer hover:bg-gray-100',
                            !isProvisioned ? 'bg-primary/10' : 'bg-gray-50/50'
                          )}
                          onClick={() =>
                            canProvision &&
                            isProvisioned &&
                            handleUnprovision(identity.id)
                          }
                        >
                          <input
                            type="radio"
                            name={`role-${identity.id}`}
                            checked={!isProvisioned}
                            readOnly
                            className="h-4 w-4 pointer-events-none"
                          />
                        </TableCell>
                        {/* Roles */}
                        {orderedRoles.map((role) => {
                          const isSelected = identity.roleId === role.id
                          return (
                            <TableCell
                              key={role.id}
                              className={cn(
                                'text-center select-none',
                                canProvision && 'cursor-pointer',
                                isSelected
                                  ? 'bg-primary/10'
                                  : role.schedulable
                                    ? canProvision
                                      ? 'bg-cyan-50/30 hover:bg-cyan-100/50'
                                      : 'bg-cyan-50/30'
                                    : canProvision
                                      ? 'bg-gray-50/30 hover:bg-gray-100'
                                      : 'bg-gray-50/30'
                              )}
                              onClick={() => {
                                if (canProvision && !isSelected)
                                  handleProvision(identity.id, role.id)
                              }}
                            >
                              <input
                                type="radio"
                                name={`role-${identity.id}`}
                                checked={isSelected}
                                readOnly
                                className="h-4 w-4 pointer-events-none"
                              />
                            </TableCell>
                          )
                        })}
                        {/* Accreditation — pinned to the right edge */}
                        <TableCell
                          className="sticky bg-background z-10 px-2 border-l"
                          style={{
                            width: PIN_WIDTH.accreditation,
                            minWidth: PIN_WIDTH.accreditation,
                            right: PIN_RIGHT.accreditation,
                          }}
                        >
                          {isProvisioned && (
                            <div style={{ width: PIN_CONTROL_WIDTH }}>
                              <select
                                className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs"
                                value={identity.accreditationLevelId ?? DEFAULT_ACCREDITATION_ID}
                                onChange={(e) => handleAccreditationChange(identity.id, e.target.value)}
                              >
                                <option value={DEFAULT_ACCREDITATION_ID}>Role Default</option>
                                {accreditationLevels.map((a) => (
                                  <option key={a.id} value={a.id}>{a.name}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </TableCell>
                        {/* Secondary Role — pinned to the right edge */}
                        <TableCell
                          className="sticky bg-background z-10 px-2"
                          style={{
                            width: PIN_WIDTH.secondaryRole,
                            minWidth: PIN_WIDTH.secondaryRole,
                            right: PIN_RIGHT.secondaryRole,
                          }}
                        >
                          {isProvisioned && (
                            <div style={{ width: PIN_CONTROL_WIDTH }}>
                              <Input
                                className="h-8 text-xs"
                                defaultValue={identity.secondaryRoleName || ''}
                                onBlur={(e) => handleSecondaryRoleBlur(identity.id, e.target.value)}
                                placeholder="Optional"
                              />
                            </div>
                          )}
                        </TableCell>
                        {/* Print Badge — pinned to the right edge */}
                        <TableCell
                          className="sticky bg-background z-10 px-2"
                          style={{
                            width: PIN_WIDTH.print,
                            minWidth: PIN_WIDTH.print,
                            right: PIN_RIGHT.print,
                          }}
                        >
                          {isProvisioned && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => handlePrintBadge(identity.id)}
                              title="Print Badge"
                            >
                              <Printer className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>

              {/* Show more / pagination */}
              <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-sm text-muted-foreground">
                  Showing {visibleIdentities.length} of {filtered.length} identities
                </span>
                {filtered.length > MAX_VISIBLE && !showAll && (
                  <Button variant="outline" size="sm" onClick={() => setShowAll(true)}>
                    Show All ({filtered.length})
                  </Button>
                )}
                {showAll && filtered.length > MAX_VISIBLE && (
                  <Button variant="outline" size="sm" onClick={() => setShowAll(false)}>
                    Show Less
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Bulk Provision Dialog */}
      <AlertDialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bulk Provision</AlertDialogTitle>
            <AlertDialogDescription>
              This will assign all {unassignedCount} unassigned identities to the role "
              {orderedRoles.find((r) => r.id === bulkRoleId)?.internalName}". Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkProvision} disabled={bulkSaving}>
              {bulkSaving ? 'Provisioning...' : `Provision ${unassignedCount} Identities`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Identity Dialog */}
      <CreateIdentityDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        clients={clients}
        // Defaults the client to this event's and offers the event's roles, so a
        // new person can be created and provisioned in one pass.
        provisioningEvent={event}
        onCreated={async (newIdentityId) => {
          // The dialog only captures name/email/role; open the full profile in a
          // new tab so the rest (photo, contact details) can be filled in without
          // losing your place in the grid.
          window.open(`/identity/${newIdentityId}`, '_blank', 'noopener,noreferrer')

          // Reload the identity list to include the new identity
          if (!eventId) return
          try {
            const identityData = await IdentityEndpoints.getPartialIdentityList(parseInt(eventId), true)
            setIdentities(identityData)
            toast.success('Identity created and added to list')
          } catch {
            // Still close dialog, identity was created
          }
        }}
      />

      {/* Batch Badge Print Dialog */}
      {eventId && (
        <BatchBadgePrintDialog
          open={batchDialogOpen}
          onOpenChange={setBatchDialogOpen}
          eventId={parseInt(eventId)}
        />
      )}
    </div>
  )
}

// ─── Batch Badge Print Dialog ──────────────────────────────────────────

interface BatchBadgePrintDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  eventId: number
}

interface BatchPdf {
  page: number
  blob: Blob
  url: string
}

const BatchBadgePrintDialog: React.FC<BatchBadgePrintDialogProps> = ({
  open,
  onOpenChange,
  eventId,
}) => {
  const [totalBadges, setTotalBadges] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [limit, setLimit] = useState(100)
  const [currentPage, setCurrentPage] = useState(0)
  const [pdfs, setPdfs] = useState<BatchPdf[]>([])
  const [running, setRunning] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cancelledRef = useRef(false)

  // Start processing when dialog opens
  useEffect(() => {
    if (!open) return

    // Reset state
    setTotalBadges(0)
    setTotalPages(0)
    setCurrentPage(0)
    setPdfs([])
    setRunning(false)
    setDone(false)
    setError(null)
    cancelledRef.current = false

    const run = async () => {
      try {
        const info = await EventEndpoints.getBulkBadgeInfo(eventId)
        if (info.total === 0) {
          setError('No badges to print')
          return
        }
        setTotalBadges(info.total)
        setTotalPages(info.pages)
        setLimit(info.limit)
        setRunning(true)

        for (let page = 0; page < info.pages; page++) {
          if (cancelledRef.current) break
          setCurrentPage(page)

          const response = await EventEndpoints.getBulkBadgePdf(eventId, page, info.limit)
          const blob = await response.blob()
          const url = URL.createObjectURL(blob)

          setPdfs((prev) => [...prev, { page, blob, url }])
        }

        if (!cancelledRef.current) {
          setDone(true)
        }
      } catch (err) {
        setError('Failed to generate badges')
        console.error('Batch badge error:', err)
      } finally {
        setRunning(false)
      }
    }

    run()
  }, [open, eventId])

  // Cleanup object URLs when dialog closes
  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      cancelledRef.current = true
      pdfs.forEach((p) => URL.revokeObjectURL(p.url))
      setPdfs([])
    }
    onOpenChange(isOpen)
  }

  const handleCancel = () => {
    cancelledRef.current = true
  }

  const handleOpenPdf = (url: string) => {
    window.open(url, '_blank')
  }

  const handleOpenAll = () => {
    // Browsers block multiple window.open() calls from a single click.
    // Open each with a staggered delay. The first one opens immediately
    // (trusted user gesture), the rest use setTimeout which some browsers
    // may still block — but most allow it for a short window after interaction.
    pdfs.forEach((p, i) => {
      if (i === 0) {
        window.open(p.url, '_blank')
      } else {
        setTimeout(() => window.open(p.url, '_blank'), i * 500)
      }
    })
    if (pdfs.length > 1) {
      toast.info(`Opening ${pdfs.length} PDFs — if some don't open, allow pop-ups for this site`)
    }
  }

  const progressPercent =
    totalPages > 0 ? Math.round((pdfs.length / totalPages) * 100) : 0

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Print All Badges
          </DialogTitle>
          {totalBadges > 0 && (
            <DialogDescription>
              {totalBadges} badge{totalBadges !== 1 ? 's' : ''} across{' '}
              {totalPages} batch{totalPages !== 1 ? 'es' : ''} ({limit} per
              batch)
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {totalPages > 0 && (
            <>
              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {done
                      ? 'Complete'
                      : running
                        ? `Generating batch ${currentPage + 1} of ${totalPages}...`
                        : cancelledRef.current
                          ? 'Cancelled'
                          : 'Starting...'}
                  </span>
                  <span className="font-medium">{progressPercent}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-300',
                      done
                        ? 'bg-green-500'
                        : cancelledRef.current
                          ? 'bg-yellow-500'
                          : 'bg-primary'
                    )}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              {/* Completed batches list */}
              {pdfs.length > 0 && (
                <div className="max-h-[250px] overflow-y-auto space-y-1">
                  {pdfs.map((pdf) => {
                    const start = pdf.page * limit + 1
                    const end = Math.min((pdf.page + 1) * limit, totalBadges)
                    return (
                      <div
                        key={pdf.page}
                        className="flex items-center justify-between rounded-md px-3 py-2 bg-muted/50 text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-600" />
                          <span>
                            Batch {pdf.page + 1}{' '}
                            <span className="text-muted-foreground">
                              ({start}-{end})
                            </span>
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7"
                          onClick={() => handleOpenPdf(pdf.url)}
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          Open
                        </Button>
                      </div>
                    )
                  })}
                  {running && (
                    <div className="flex items-center gap-2 rounded-md px-3 py-2 bg-muted/30 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>
                        Batch {currentPage + 1} ({currentPage * limit + 1}-
                        {Math.min((currentPage + 1) * limit, totalBadges)})
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {!totalPages && !error && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 sm:justify-between">
          <div>
            {running && (
              <Button variant="outline" onClick={handleCancel}>
                <X className="mr-1 h-4 w-4" />
                Cancel
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {pdfs.length > 0 && (
              <Button onClick={handleOpenAll}>
                <Printer className="mr-1 h-4 w-4" />
                Open All ({pdfs.length})
              </Button>
            )}
            {(done || error || (!running && cancelledRef.current)) && (
              <Button variant="outline" onClick={() => handleClose(false)}>
                Close
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
